import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, UserCheck, Clock3, CircleAlert } from "lucide-react";
import { BrandMark } from "@/components/brand/BrandMark";
import DoxaSelect from "@/components/doxa/DoxaSelect";

const PRIVACY_TEXT =
  "A Doxa School utilizará seus dados para gestão da turma, controle de presença, acompanhamento de progresso, envio de avisos, organização de materiais e acompanhamento interno da formação. Seus dados serão acessados apenas por pessoas autorizadas.";

const ROLE_LABEL = { MASTER: "Master", ADMIN: "Admin", PROFESSOR: "Professor", STUDENT: "Aluno" };

export default function Register() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [existing, setExisting] = useState(null);
  const [existingLoading, setExistingLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", birth_date: "", cell_group: "", password: "", password2: "" });

  useEffect(() => {
    api.get(`/register/${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.cells?.length) setForm((f) => ({ ...f, cell_group: data.cells[0] }));
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (authLoading || !user) {
      setExisting(null);
      return;
    }
    setExistingLoading(true);
    api.get(`/register/${token}/me`)
      .then(({ data }) => setExisting(data))
      .catch((err) => setExisting({ state: "ERROR", message: formatApiError(err) }))
      .finally(() => setExistingLoading(false));
  }, [authLoading, user, token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) return toast.error("Você precisa aceitar o aviso de uso de dados.");
    if (form.password.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres.");
    if (form.password !== form.password2) return toast.error("As senhas não conferem.");
    if (!form.cell_group) return toast.error("Selecione sua célula.");
    setSubmitting(true);
    try {
      await api.post(`/register/${token}`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        birth_date: form.birth_date || null,
        cell_group: form.cell_group,
        password: form.password,
        privacy_accepted: true,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const requestExistingEnrollment = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/register/${token}/request`);
      setExisting((current) => ({ ...current, state: data.state, request_id: data.request_id }));
      if (data.state === "PENDING") toast.success("Solicitação enviada para aprovação!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading || (user && existingLoading)) return <div className="min-h-screen flex items-center justify-center text-[#6B655E]">Carregando…</div>;

  if (!info) return (
    <div className="min-h-screen bg-[#F8F3EA] flex items-center justify-center p-6">
      <div className="doxa-card p-8 max-w-md w-full text-center" data-testid="register-invalid">
        <p className="font-display text-xl font-bold">Inscrição indisponível</p>
        <p className="text-[#6B655E] mt-2 text-sm">Este link de inscrição é inválido ou foi desativado. Procure a equipe da Doxa School.</p>
        <Link to="/login" className="inline-block mt-4 text-sm text-[#F75A1B] font-medium">Ir para o login</Link>
      </div>
    </div>
  );

  if (user && existing) {
    const state = existing.state;
    const student = existing.student;

    return (
      <div className="min-h-screen bg-[#F8F3EA] flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <BrandMark size="lg" />
            <div>
              <p className="font-display text-xl font-bold">Doxa School</p>
              <p className="text-xs text-[#6B655E]">Inscrição em turma</p>
            </div>
          </div>

          <div className="doxa-card p-6 md:p-8" data-testid="existing-student-registration">
            {state === "ELIGIBLE" && (
              <>
                <UserCheck className="w-11 h-11 text-[#F75A1B]" />
                <h1 className="font-display text-2xl md:text-3xl font-bold mt-4">Solicitar inscrição</h1>
                <p className="mt-2 text-[#6B655E]">
                  Você já possui cadastro na Doxa School. Não precisa preencher tudo novamente.
                </p>
                <div className="mt-5 p-4 rounded-xl bg-[#F6EFE4] border border-[#E0D9CD] space-y-2 text-sm">
                  <p><span className="text-[#6B655E]">Aluno:</span> <strong>{student?.full_name}</strong></p>
                  <p><span className="text-[#6B655E]">E-mail:</span> {student?.email}</p>
                  <p><span className="text-[#6B655E]">Célula:</span> {student?.cell_group || "—"}</p>
                  {student?.current_class_group_name && <p><span className="text-[#6B655E]">Turma atual:</span> {student.current_class_group_name}</p>}
                  <p><span className="text-[#6B655E]">Nova turma:</span> <strong>{existing.class_group_name}</strong></p>
                </div>
                <p className="mt-4 text-sm text-[#6B655E]">
                  A solicitação seguirá para aprovação da equipe. Seus dados cadastrais permanecem como estão.
                </p>
                <button onClick={requestExistingEnrollment} disabled={submitting}
                  className="doxa-btn-primary w-full py-3 mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="existing-student-request">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Solicitar inscrição
                </button>
              </>
            )}

            {state === "PENDING" && (
              <div className="text-center" data-testid="existing-student-pending">
                <Clock3 className="w-12 h-12 text-[#D97706] mx-auto" />
                <h1 className="font-display text-2xl font-bold mt-4">Solicitação em análise</h1>
                <p className="text-[#6B655E] mt-2 text-sm">
                  Sua solicitação para <strong className="text-[#1F1F1F]">{existing.class_group_name}</strong> já foi enviada e aguarda aprovação da equipe da Doxa School.
                </p>
                <Link to="/aluno" className="inline-block mt-5 doxa-btn-primary px-5 py-2.5">Voltar ao Portal do Aluno</Link>
              </div>
            )}

            {state === "ALREADY_ENROLLED" && (
              <div className="text-center" data-testid="existing-student-already-enrolled">
                <CheckCircle2 className="w-12 h-12 text-[#2F9E44] mx-auto" />
                <h1 className="font-display text-2xl font-bold mt-4">Você já está nesta turma</h1>
                <p className="text-[#6B655E] mt-2 text-sm">
                  Seu cadastro já está vinculado à turma <strong className="text-[#1F1F1F]">{existing.class_group_name}</strong>.
                </p>
                <Link to="/aluno" className="inline-block mt-5 doxa-btn-primary px-5 py-2.5">Ir para o Portal do Aluno</Link>
              </div>
            )}

            {state === "ROLE_NOT_SUPPORTED" && (
              <div className="text-center" data-testid="existing-registration-role-blocked">
                <CircleAlert className="w-12 h-12 text-[#D97706] mx-auto" />
                <h1 className="font-display text-2xl font-bold mt-4">Use uma conta de aluno</h1>
                <p className="text-[#6B655E] mt-2 text-sm">
                  Você está conectado como <strong>{ROLE_LABEL[existing.role] || existing.role}</strong>. Para solicitar inscrição nessa turma, abra o link usando a conta de aluno correspondente ou procure a equipe da Doxa School.
                </p>
                <Link to="/" className="inline-block mt-5 text-sm text-[#F75A1B] font-medium">Voltar ao sistema</Link>
              </div>
            )}

            {(state === "ACADEMIC_INACTIVE" || state === "STUDENT_RECORD_MISSING" || state === "ERROR") && (
              <div className="text-center" data-testid="existing-registration-blocked">
                <CircleAlert className="w-12 h-12 text-[#C53030] mx-auto" />
                <h1 className="font-display text-2xl font-bold mt-4">Não foi possível solicitar agora</h1>
                <p className="text-[#6B655E] mt-2 text-sm">
                  {state === "ACADEMIC_INACTIVE"
                    ? "Seu cadastro acadêmico não está ativo. Procure a equipe da Doxa School para regularizar antes de solicitar uma nova turma."
                    : state === "STUDENT_RECORD_MISSING"
                      ? "Sua conta não possui um cadastro acadêmico de aluno vinculado. Procure a equipe da Doxa School."
                      : (existing.message || "Não foi possível verificar sua inscrição.")}
                </p>
                <Link to="/" className="inline-block mt-5 text-sm text-[#F75A1B] font-medium">Voltar ao sistema</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (done) return (
    <div className="min-h-screen bg-[#F8F3EA] flex items-center justify-center p-6">
      <div className="doxa-card p-8 max-w-md w-full text-center" data-testid="register-success">
        <CheckCircle2 className="w-12 h-12 text-[#2F9E44] mx-auto" />
        <h1 className="font-display text-2xl font-bold mt-4">Inscrição enviada!</h1>
        <p className="text-[#6B655E] mt-2 text-sm">
          Recebemos sua inscrição para <span className="font-semibold text-[#1F1F1F]">{info.class_group_name}</span>.
          Assim que a equipe da Doxa School aprovar, você poderá entrar com seu e-mail e senha.
        </p>
        <Link to="/login" className="inline-block mt-5 doxa-btn-primary px-5 py-2.5" data-testid="register-go-login">Ir para o login</Link>
      </div>
    </div>
  );

  const cells = info.cells || [];

  return (
    <div className="min-h-screen bg-[#F8F3EA] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <BrandMark size="lg" />
          <div>
            <p className="font-display text-xl font-bold">Doxa School</p>
            <p className="text-xs text-[#6B655E]">Inscrição</p>
          </div>
        </div>

        <div className="doxa-card p-6 md:p-8" data-testid="register-page">
          <h1 className="font-display text-2xl md:text-3xl font-bold">Faça sua inscrição</h1>
          <p className="mt-2 text-[#6B655E]">
            Turma: <span className="font-semibold text-[#1F1F1F]">{info.class_group_name}</span>
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Nome completo</label>
              <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                data-testid="register-full_name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">E-mail</label>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                data-testid="register-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Telefone/WhatsApp</label>
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  data-testid="register-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data de nascimento</label>
                <input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  data-testid="register-birth_date" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Célula</label>
              {cells.length === 0 ? (
                <p className="text-xs text-[#C53030] p-2 bg-[#FED7D7] rounded-lg">Nenhuma célula disponível no momento. Procure a equipe da Doxa School.</p>
              ) : (
                <DoxaSelect
                  value={form.cell_group}
                  onValueChange={(value) => set("cell_group", value)}
                  options={cells.map((cell) => ({ value: cell, label: cell }))}
                  placeholder="Selecione sua célula"
                  ariaLabel="Célula"
                  testId="register-cell_group"
                  className="h-[46px] w-full min-w-0"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Crie uma senha</label>
                <input required type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  data-testid="register-password" placeholder="mín. 6 caracteres" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Confirme a senha</label>
                <input required type="password" value={form.password2} onChange={(e) => set("password2", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B]"
                  data-testid="register-password2" />
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#F6EFE4] border border-[#E0D9CD]">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-[#F75A1B] shrink-0 mt-0.5" />
                <p className="text-sm text-[#1F1F1F] leading-relaxed">{PRIVACY_TEXT}</p>
              </div>
              <label className="mt-3 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#F75A1B]" data-testid="register-privacy" />
                <span className="text-sm">Li e aceito o aviso de uso de dados.</span>
              </label>
            </div>

            <button type="submit" disabled={submitting || cells.length === 0}
              className="doxa-btn-primary w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enviar inscrição
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-[#6B655E] mt-4">
          Já tem cadastro? <Link to="/login" className="text-[#F75A1B] font-medium">Faça login</Link>
        </p>
      </div>
    </div>
  );
}
